import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Copy, ExternalLink, MapPin, RefreshCw, Settings, X } from 'lucide-react';
import type { LocationDiagnostics } from '../types';
import type { LocationIssue } from '../hooks/useQueueJoinFlow';
import '../styles/captcha.css';

interface LocationCheckModalProps {
  issue: LocationIssue;
  onRetry: () => void;
  onClose: () => void;
  /** Opens Telegram's location settings for this bot */
  onOpenSettings: () => void;
}

interface LocationFailedModalProps {
  diagnostics: LocationDiagnostics;
  retrying: boolean;
  onRetry: () => void;
  onClose: () => void;
}

interface LocationUnavailableModalProps {
  reason: 'denied' | 'unavailable' | 'unsupported';
  canOpenSettings: boolean;
  retrying: boolean;
  onRetry: () => void;
  onClose: () => void;
  onOpenSettings: () => void;
}

const formatDistance = (meters: number | null, t: (key: string, opts?: any) => string) =>
  meters == null
    ? '—'
    : meters < 1000
      ? t('queueAccess.locationPopup.meters', { value: Math.round(meters) })
      : t('queueAccess.locationPopup.kilometers', { value: String(Math.round(meters / 100) / 10) });

export function googleMapsUrl(d: LocationDiagnostics) {
  return `https://maps.google.com/?q=${d.latitude.toFixed(6)},${d.longitude.toFixed(6)}`;
}

/** One-line summary a user can paste to support. */
export function describeDiagnostics(d: LocationDiagnostics) {
  return [
    'Location check',
    `reason=${d.reason}`,
    `coords=${d.latitude.toFixed(6)},${d.longitude.toFixed(6)}`,
    `accuracy=${d.accuracyMeters ?? 'unknown'} m`,
    `distance=${d.distanceMeters ?? '?'} m (max ${d.radiusMeters} m)`,
    `age=${d.ageSeconds ?? '?'} s`,
    `at=${new Date().toISOString()}`,
  ].join('; ');
}

function openExternal(url: string) {
  const webApp = (window as any).Telegram?.WebApp;
  if (webApp?.initData && typeof webApp.openLink === 'function') {
    webApp.openLink(url);
  } else {
    window.open(url, '_blank', 'noopener');
  }
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older webviews: fall back to a temporary textarea
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  }
}

/**
 * Location pop-up for shift 1: either the phone gave no location at all (permission or GPS
 * problem), or the location it gave did not pass the check.
 */
export function LocationCheckModal({ issue, onRetry, onClose, onOpenSettings }: LocationCheckModalProps) {
  return issue.kind === 'failed' ? (
    <LocationFailedModal diagnostics={issue.diagnostics} retrying={issue.retrying} onRetry={onRetry} onClose={onClose} />
  ) : (
    <LocationUnavailableModal
      reason={issue.reason}
      canOpenSettings={issue.canOpenSettings}
      retrying={issue.retrying}
      onRetry={onRetry}
      onClose={onClose}
      onOpenSettings={onOpenSettings}
    />
  );
}

/**
 * No location could be read. Most often Telegram location access is off for this bot:
 * Telegram then never asks again, so the user must re-allow it in the bot's settings.
 */
function LocationUnavailableModal({ reason, canOpenSettings, retrying, onRetry, onClose, onOpenSettings }: LocationUnavailableModalProps) {
  const { t } = useTranslation();
  const steps = t(`queueAccess.locationPopup.unavailable.${reason}.steps`, { returnObjects: true }) as unknown;

  return (
    <div className="modal-overlay-modern captcha-overlay">
      <div className="modal-modern location-check-modal" role="dialog" aria-modal="true" aria-labelledby="location-unavailable-title">
        <div className="location-check-header">
          <MapPin size={20} className="location-check-icon" aria-hidden="true" />
          <div className="location-check-heading">
            <h3 id="location-unavailable-title">{t('queueAccess.locationPopup.unavailable.title')}</h3>
            <p>{t(`queueAccess.locationPopup.unavailable.${reason}.body`)}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label={t('common.close')}>
            <X size={20} />
          </button>
        </div>

        {Array.isArray(steps) && (
          <div className="location-check-body">
            <ol className="captcha-steps location-unavailable-steps">
              {steps.map((step: string, index: number) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        <div className="location-check-actions">
          {canOpenSettings && (
            <button className="btn-modern btn-primary-modern location-check-wide" onClick={onOpenSettings}>
              <Settings size={16} /> {t('queueAccess.locationPopup.unavailable.openSettings')}
            </button>
          )}
          <button
            className={`btn-modern ${canOpenSettings ? 'btn-secondary-modern' : 'btn-primary-modern'} location-check-wide`}
            onClick={onRetry}
            disabled={retrying}
          >
            <RefreshCw size={16} className={retrying ? 'animate-spin' : undefined} />
            {retrying ? t('queueAccess.locationPopup.retrying') : t('queueAccess.locationPopup.retry')}
          </button>
          <button className="btn-modern btn-secondary-modern location-check-wide" onClick={onClose}>
            {t('queueAccess.locationPopup.laterShift')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Shown when the location check fails: where the phone says the user is, how accurate that
 * is, and how far it is from MATAF, on a map. Only the public location is ever shown.
 */
function LocationFailedModal({ diagnostics: d, retrying, onRetry, onClose }: LocationFailedModalProps) {
  const { t } = useTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!mapRef.current) return;

    const map = L.map(mapRef.current, { zoomControl: false, attributionControl: true, scrollWheelZoom: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(map);

    const site = L.latLng(d.siteLatitude, d.siteLongitude);
    const user = L.latLng(d.latitude, d.longitude);

    L.circle(site, { radius: d.radiusMeters, color: '#1D9E75', weight: 1.5, dashArray: '5 4', fillOpacity: 0.12 }).addTo(map);
    L.circleMarker(site, { radius: 6, color: '#ffffff', weight: 2, fillColor: '#0F6E56', fillOpacity: 1 })
      .bindTooltip(t('queueAccess.locationPopup.siteLabel'), { permanent: true, direction: 'bottom', offset: [0, 6] })
      .addTo(map);

    if (d.accuracyMeters) {
      L.circle(user, { radius: d.accuracyMeters, color: '#378ADD', weight: 1, fillOpacity: 0.15 }).addTo(map);
    }
    L.circleMarker(user, { radius: 7, color: '#ffffff', weight: 2.5, fillColor: '#185FA5', fillOpacity: 1 })
      .bindTooltip(t('queueAccess.locationPopup.youLabel'), { permanent: true, direction: 'top', offset: [0, -8] })
      .addTo(map);
    L.polyline([user, site], { color: '#185FA5', weight: 1.2, dashArray: '3 3' }).addTo(map);

    // Frame the MATAF area and the user's dot (not a city-sized accuracy circle). Computed from
    // the coordinates: a circle's own getBounds() needs a map view that doesn't exist yet.
    const bounds = site.toBounds(d.radiusMeters * 2).extend(user);
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 });

    return () => {
      map.remove();
    };
  }, [d, t]);

  const reasonText = (() => {
    switch (d.reason) {
      case 'Outside':
        return t('queueAccess.locationPopup.reasonOutside', {
          distance: formatDistance(d.distanceMeters, t),
          radius: formatDistance(d.radiusMeters, t),
        });
      case 'Inaccurate':
        return t('queueAccess.locationPopup.reasonInaccurate', {
          accuracy: formatDistance(d.accuracyMeters, t),
          max: formatDistance(d.maxAccuracyMeters, t),
        });
      case 'AccuracyUnknown':
        return t('queueAccess.locationPopup.reasonAccuracyUnknown');
      default:
        return t('queueAccess.locationPopup.reasonStale', { seconds: Math.max(0, Math.round(d.ageSeconds ?? 0)) });
    }
  })();

  const handleCopy = async () => {
    setCopied(await copyText(describeDiagnostics(d)));
  };

  return (
    <div className="modal-overlay-modern captcha-overlay">
      <div className="modal-modern location-check-modal" role="dialog" aria-modal="true" aria-labelledby="location-check-title">
        <div className="location-check-header">
          <MapPin size={20} className="location-check-icon" aria-hidden="true" />
          <div className="location-check-heading">
            <h3 id="location-check-title">{t('queueAccess.locationPopup.title')}</h3>
            <p>{reasonText}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label={t('common.close')}>
            <X size={20} />
          </button>
        </div>

        <div ref={mapRef} className="location-check-map" />

        <div className="location-check-body">
          <dl className="location-check-facts">
            <dt>{t('queueAccess.locationPopup.coordinates')}</dt>
            <dd className="location-check-coords" dir="ltr">
              {d.latitude.toFixed(5)}, {d.longitude.toFixed(5)}
            </dd>
            <dt>{t('queueAccess.locationPopup.accuracy')}</dt>
            <dd>{d.accuracyMeters ? `±${formatDistance(d.accuracyMeters, t)}` : t('queueAccess.locationPopup.unknown')}</dd>
            <dt>{t('queueAccess.locationPopup.distance')}</dt>
            <dd className={d.reason === 'Outside' ? 'location-check-bad' : undefined}>
              {formatDistance(d.distanceMeters, t)} ({t('queueAccess.locationPopup.max', { radius: formatDistance(d.radiusMeters, t) })})
            </dd>
            <dt>{t('queueAccess.locationPopup.taken')}</dt>
            <dd>{t('queueAccess.locationPopup.secondsAgo', { seconds: Math.max(0, Math.round(d.ageSeconds ?? 0)) })}</dd>
          </dl>
          <p className="location-check-tip">{t('queueAccess.locationPopup.tip')}</p>
        </div>

        <div className="location-check-actions">
          <button className="btn-modern btn-primary-modern location-check-wide" onClick={onRetry} disabled={retrying}>
            <RefreshCw size={16} className={retrying ? 'animate-spin' : undefined} />
            {retrying ? t('queueAccess.locationPopup.retrying') : t('queueAccess.locationPopup.retry')}
          </button>
          <button className="btn-modern btn-secondary-modern" onClick={() => openExternal(googleMapsUrl(d))}>
            <ExternalLink size={16} /> {t('queueAccess.locationPopup.googleMaps')}
          </button>
          <button className="btn-modern btn-secondary-modern" onClick={handleCopy}>
            <Copy size={16} /> {copied ? t('queueAccess.locationPopup.copied') : t('queueAccess.locationPopup.copy')}
          </button>
          <button className="btn-modern btn-secondary-modern location-check-wide" onClick={onClose}>
            {t('queueAccess.locationPopup.laterShift')}
          </button>
        </div>
      </div>
    </div>
  );
}
